import React from "react";
import {
  Box,
  Stack,
  Typography,
  ButtonBase,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from "@mui/material";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PetsIcon from "@mui/icons-material/Pets";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import type { NavKey } from "../types";
import { QuizStyledCard } from "../components/shared/QuizStyledCard";
import { PageTitle } from "../components/shared/PageTitle";
import { QUIZ } from "@/theme/quizTokens";

const SUPPORT_EMAIL = "support@pawsonplates.com";

const HELP_LINKS: {
  nav: NavKey;
  title: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    nav: "subscriptions",
    title: "Manage your subscription",
    description: "Change recipes, serving size, frequency, or pause your plan",
    icon: <RestaurantMenuIcon sx={{ fontSize: 22, color: QUIZ.color.ink }} />,
  },
  {
    nav: "upcoming",
    title: "Upcoming deliveries",
    description: "See what's coming, change dates, or skip a delivery",
    icon: <LocalShippingIcon sx={{ fontSize: 22, color: QUIZ.color.ink }} />,
  },
  {
    nav: "dogs",
    title: "Your dog's profile",
    description: "Update weight, age, activity, allergies, or add a dog",
    icon: <PetsIcon sx={{ fontSize: 22, color: QUIZ.color.ink }} />,
  },
  {
    nav: "orders",
    title: "Order history",
    description: "Past orders, tracking, and order details",
    icon: <ReceiptLongIcon sx={{ fontSize: 22, color: QUIZ.color.ink }} />,
  },
  {
    nav: "address",
    title: "Delivery address",
    description: "Update where your boxes are delivered",
    icon: <HomeIcon sx={{ fontSize: 22, color: QUIZ.color.ink }} />,
  },
  {
    nav: "payments",
    title: "Payment methods",
    description: "Update your card or billing details",
    icon: <CreditCardIcon sx={{ fontSize: 22, color: QUIZ.color.ink }} />,
  },
  {
    nav: "extras",
    title: "Extras & add-ons",
    description: "Add treats and one-off extras to your next box",
    icon: <AddShoppingCartIcon sx={{ fontSize: 22, color: QUIZ.color.ink }} />,
  },
];

// Q&As sourced from the marketing site's FAQ sections (pop-faq)
const FAQS: { question: string; answer: string }[] = [
  {
    question: "How does the subscription work?",
    answer:
      "Tell us about your dog and we'll build a tailored plan, delivered on a schedule that suits you. Pause, skip or cancel any time.",
  },
  {
    question: "How much should I feed my dog?",
    answer:
      "Every plan is portioned to your dog's breed, age and weight, with no scoops or guesswork. We adjust portions as they grow.",
  },
  {
    question: "How is the food delivered?",
    answer:
      "Frozen and packed plastic-free, delivered straight to your door the next day.",
  },
  {
    question: "Will switching upset their stomach?",
    answer:
      "We give you a simple slow-transition plan, and most owners report zero tummy trouble. You're never doing it alone. Message us any time.",
  },
  {
    question: "What if my dog is really fussy?",
    answer:
      "That's exactly who this is for. Start with a small box and see for yourself, plus try risk-free with our 30-day money back guarantee.",
  },
  {
    question: "Is the food suitable for puppies?",
    answer:
      "Yes. Our recipes are complete and balanced for every life stage, with plans tailored to your dog's age and needs.",
  },
];

function HelpLinkCard({
  title,
  description,
  icon,
  onClick,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        background: QUIZ.color.warmWhite,
        border: "1px solid rgba(0, 0, 0, 0.08)",
        borderRadius: "12px",
        p: 1.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.5,
        textAlign: "left",
        width: "100%",
        transition: "background 150ms ease",
        "&:hover": { background: QUIZ.color.cream },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          background: QUIZ.color.blue,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 15, fontWeight: 700, color: QUIZ.color.ink, lineHeight: 1.2 }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: QUIZ.color.grey, opacity: 0.85, lineHeight: 1.35, mt: 0.25 }}
        >
          {description}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

export function HelpPage({ onNavigate }: { onNavigate: (key: NavKey) => void }) {
  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      <QuizStyledCard>
        <PageTitle
          title="Help & Support"
          subtitle="Quick answers to common questions"
        />

        {/* Quick links into the account */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
            gap: 1.5,
            mt: 3,
          }}
        >
          {HELP_LINKS.map((link) => (
            <HelpLinkCard
              key={link.nav}
              title={link.title}
              description={link.description}
              icon={link.icon}
              onClick={() => onNavigate(link.nav)}
            />
          ))}
        </Box>

        {/* Contact */}
        <Box
          sx={{
            mt: 4,
            background: QUIZ.color.warmWhite,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: QUIZ.color.blue,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <EmailIcon sx={{ fontSize: 22, color: QUIZ.color.ink }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: QUIZ.color.ink }}>
              Need more help?
            </Typography>
            <Typography sx={{ fontSize: 13, color: QUIZ.color.grey, opacity: 0.85 }}>
              Can't find what you're looking for? Our friendly support team is here to help.
            </Typography>
          </Box>
          <Link
            href={`mailto:${SUPPORT_EMAIL}`}
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: QUIZ.color.brown,
              textDecorationColor: QUIZ.color.brown,
              "&:hover": { color: QUIZ.color.ink },
            }}
          >
            {SUPPORT_EMAIL}
          </Link>
        </Box>

        {/* FAQs */}
        <Typography
          sx={{
            fontFamily: QUIZ.font.display,
            fontSize: { xs: 22, md: 26 },
            color: QUIZ.color.ink,
            textTransform: "uppercase",
            mt: 4,
            mb: 2,
          }}
        >
          FAQs
        </Typography>
        <Box>
          {FAQS.map((faq) => (
            <Accordion
              key={faq.question}
              disableGutters
              elevation={0}
              sx={{
                background: QUIZ.color.warmWhite,
                border: "1px solid rgba(0, 0, 0, 0.08)",
                borderRadius: "12px !important",
                mb: 1,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: QUIZ.color.brown }} />}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: QUIZ.color.ink }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography sx={{ fontSize: 14, color: QUIZ.color.grey, lineHeight: 1.5 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </QuizStyledCard>
    </Stack>
  );
}
